import { useRef } from "react";
import {
  MASZYNY,
  MASZYNY_PAYLINES,
  maszynyCellKey,
  randomMaszynySymbol,
  type MaszynyPaylineId,
  type MaszynySymbol,
} from "@ziolo/shared";

export type MaszynyReelPhase =
  | "idle"
  | "accelerating"
  | "fast"
  | "decelerating"
  | "stopping";

export interface MaszynyBoardProps {
  grid: MaszynySymbol[][];
  phases: MaszynyReelPhase[];
  winningKeys: ReadonlySet<string>;
  scatterKeys: ReadonlySet<string>;
  activeLines: readonly MaszynyPaylineId[];
}

/** Extra symbols above the window so a stop can actually scroll into place. */
export const MASZYNY_REEL_FILLERS = 12;

/** Matches `--gap: calc(var(--cell) * 0.125)` so SVG centers hit reel centers. */
const GAP_RATIO = 0.125;
const VIEW_W = MASZYNY.REELS + (MASZYNY.REELS - 1) * GAP_RATIO;
const VIEW_H = MASZYNY.ROWS;

function cellCenter(reel: number, row: number): string {
  return `${reel * (1 + GAP_RATIO) + 0.5},${row + 0.5}`;
}

const SHORT_LABEL: Record<MaszynyPaylineId, string> = {
  top: "Góra",
  middle: "Środek",
  bottom: "Dół",
  v: "V",
  inv: "odwr. V",
};

function paylinePoints(rows: readonly [number, number, number, number, number]): string {
  return rows.map((row, reel) => cellCenter(reel, row)).join(" ");
}

function freshFillers(): MaszynySymbol[] {
  return Array.from({ length: MASZYNY_REEL_FILLERS }, () => randomMaszynySymbol());
}

function ReelColumn({
  reel,
  column,
  phase,
  winningKeys,
  scatterKeys,
}: {
  reel: number;
  column: readonly MaszynySymbol[];
  phase: MaszynyReelPhase;
  winningKeys: ReadonlySet<string>;
  scatterKeys: ReadonlySet<string>;
}) {
  const fillersRef = useRef<MaszynySymbol[] | null>(null);
  const prevPhaseRef = useRef(phase);

  if (fillersRef.current === null) {
    fillersRef.current = freshFillers();
  }
  if (phase === "accelerating" && prevPhaseRef.current !== "accelerating") {
    fillersRef.current = freshFillers();
  }
  prevPhaseRef.current = phase;

  const fillers = fillersRef.current ?? freshFillers();
  const strip = [...fillers, ...column];

  return (
    <div
      className={`maszyny-reel maszyny-reel--${phase}`}
      data-testid={`maszyny-reel-${reel}`}
    >
      <div className="maszyny-reel__window">
        <div className="maszyny-reel__strip">
          {strip.map((symbol, index) => {
            const row = index - fillers.length;
            const inWindow = row >= 0 && row < MASZYNY.ROWS;
            const key = inWindow ? maszynyCellKey({ reel, row }) : `pad-${reel}-${index}`;
            const classes = ["maszyny-symbol"];
            if (inWindow && winningKeys.has(key)) classes.push("maszyny-symbol--win");
            if (inWindow && scatterKeys.has(key)) classes.push("maszyny-symbol--scatter");
            return (
              <div
                key={key}
                className={classes.join(" ")}
                {...(inWindow
                  ? {
                      "data-reel": String(reel),
                      "data-row": String(row),
                      "data-testid": `maszyny-cell-${reel}-${row}`,
                    }
                  : {})}
              >
                {symbol}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function MaszynyBoard({
  grid,
  phases,
  winningKeys,
  scatterKeys,
  activeLines,
}: MaszynyBoardProps) {
  return (
    <div className="maszyny-machine">
      <div className="maszyny-reels" aria-label="Bębny">
        {Array.from({ length: MASZYNY.REELS }, (_, reel) => {
          const column = grid[reel] ?? [];
          const phase = phases[reel] ?? "idle";
          return (
            <ReelColumn
              key={reel}
              reel={reel}
              column={column}
              phase={phase}
              winningKeys={winningKeys}
              scatterKeys={scatterKeys}
            />
          );
        })}
        <svg
          className="maszyny-paylines"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          data-testid="maszyny-paylines"
        >
          {MASZYNY_PAYLINES.map((line) => {
            if (!activeLines.includes(line.id)) return null;
            return (
              <polyline
                key={line.id}
                className="maszyny-payline maszyny-payline--active"
                data-line={line.id}
                data-testid={`maszyny-payline-${line.id}`}
                points={paylinePoints(line.rows)}
                fill="none"
              />
            );
          })}
        </svg>
      </div>
      <ul className="maszyny-legend" aria-label="Linie wypłat">
        {MASZYNY_PAYLINES.map((line) => (
          <li
            key={line.id}
            title={line.label}
            className={
              activeLines.includes(line.id)
                ? "maszyny-legend__item maszyny-legend__item--active"
                : "maszyny-legend__item"
            }
          >
            <svg viewBox="0 0 5 3" aria-hidden="true">
              <polyline
                className="maszyny-legend__stroke"
                data-line={line.id}
                points={line.rows.map((row, reel) => `${reel + 0.5},${row + 0.5}`).join(" ")}
                fill="none"
              />
            </svg>
            <span>{SHORT_LABEL[line.id]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
